<template>
  <div class="kyc-address-fields">
    <div class="mb-3">
      <label
        >Provinsi <span class="text-danger">*</span></label
      >
      <select
        class="form_input w-100"
        :value="modelValue.provinceCode"
        :disabled="loadingProvinces"
        @change="onProvinceChange"
      >
        <option value="">
          {{ loadingProvinces ? "Memuat provinsi..." : "Pilih Provinsi" }}
        </option>
        <option
          v-for="item in provinces"
          :key="item.code"
          :value="item.code"
        >
          {{ item.name }}
        </option>
      </select>
    </div>

    <div class="mb-3">
      <label
        >Kabupaten / Kota <span class="text-danger">*</span></label
      >
      <select
        class="form_input w-100"
        :value="modelValue.regencyCode"
        :disabled="!modelValue.provinceCode || loadingRegencies"
        @change="onRegencyChange"
      >
        <option value="">
          {{
            loadingRegencies
              ? "Memuat kabupaten..."
              : modelValue.provinceCode
              ? "Pilih Kabupaten / Kota"
              : "Pilih provinsi terlebih dahulu"
          }}
        </option>
        <option
          v-for="item in regencies"
          :key="item.code"
          :value="item.code"
        >
          {{ item.name }}
        </option>
      </select>
    </div>

    <div class="mb-3">
      <label
        >Kecamatan <span class="text-danger">*</span></label
      >
      <select
        class="form_input w-100"
        :value="modelValue.districtCode"
        :disabled="!modelValue.regencyCode || loadingDistricts"
        @change="onDistrictChange"
      >
        <option value="">
          {{
            loadingDistricts
              ? "Memuat kecamatan..."
              : modelValue.regencyCode
              ? "Pilih Kecamatan"
              : "Pilih kabupaten terlebih dahulu"
          }}
        </option>
        <option
          v-for="item in districts"
          :key="item.code"
          :value="item.code"
        >
          {{ item.name }}
        </option>
      </select>
    </div>

    <div class="mb-3">
      <label
        >Kelurahan / Desa <span class="text-danger">*</span></label
      >
      <select
        class="form_input w-100"
        :value="modelValue.villageCode"
        :disabled="!modelValue.districtCode || loadingVillages"
        @change="onVillageChange"
      >
        <option value="">
          {{
            loadingVillages
              ? "Memuat kelurahan..."
              : modelValue.districtCode
              ? "Pilih Kelurahan / Desa"
              : "Pilih kecamatan terlebih dahulu"
          }}
        </option>
        <option
          v-for="item in villages"
          :key="item.code"
          :value="item.code"
        >
          {{ item.name }}
        </option>
      </select>
    </div>

    <div class="mb-3">
      <label
        >Kode Pos <span class="text-danger">*</span></label
      >
      <input
        class="form_input w-100"
        type="text"
        inputmode="numeric"
        maxlength="5"
        placeholder="Contoh: 10310"
        :value="modelValue.postalCode"
        @input="onPostalCodeInput"
      />
    </div>

    <div class="mb-3">
      <label
        >Alamat Lengkap <span class="text-danger">*</span></label
      >
      <textarea
        class="form_input w-100"
        rows="3"
        :value="modelValue.street"
        placeholder="Nama jalan, nomor rumah, RT/RW"
        @input="onStreetInput"
      ></textarea>
    </div>

    <p v-if="errorMessage" class="kyc-address-error mb-0">
      {{ errorMessage }}
    </p>
  </div>
</template>

<script>
import thestore from "@/applibs/thestore.js";

export default {
  name: "KycAddressFields",
  props: {
    modelValue: {
      type: Object,
      required: true,
    },
    token: {
      type: String,
      default: "",
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      provinces: [],
      regencies: [],
      districts: [],
      villages: [],
      loadingProvinces: false,
      loadingRegencies: false,
      loadingDistricts: false,
      loadingVillages: false,
      errorMessage: "",
    };
  },
  mounted() {
    this.loadProvinces();
    if (this.modelValue.provinceCode) this.loadRegencies(this.modelValue.provinceCode);
    if (this.modelValue.regencyCode) this.loadDistricts(this.modelValue.regencyCode);
    if (this.modelValue.districtCode) this.loadVillages(this.modelValue.districtCode);
  },
  methods: {
    patch(fields) {
      this.$emit("update:modelValue", { ...this.modelValue, ...fields });
    },
    resetFrom(level) {
      const cleared = {};
      if (level === "province") {
        Object.assign(cleared, {
          regencyCode: "",
          regencyName: "",
          districtCode: "",
          districtName: "",
          villageCode: "",
          villageName: "",
        });
        this.regencies = [];
        this.districts = [];
        this.villages = [];
      } else if (level === "regency") {
        Object.assign(cleared, {
          districtCode: "",
          districtName: "",
          villageCode: "",
          villageName: "",
        });
        this.districts = [];
        this.villages = [];
      } else if (level === "district") {
        Object.assign(cleared, {
          villageCode: "",
          villageName: "",
        });
        this.villages = [];
      }
      return cleared;
    },
    findName(items, code) {
      const found = items.find((item) => String(item.code) === String(code));
      return found ? found.name : "";
    },
    async loadProvinces() {
      this.loadingProvinces = true;
      this.errorMessage = "";
      try {
        const res = await thestore.getKycRegionsProvinces(this.token);
        if (res?.success) {
          this.provinces = res.data || [];
        } else {
          this.errorMessage = res?.message || "Gagal memuat data provinsi.";
        }
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.message || "Gagal memuat data provinsi.";
      } finally {
        this.loadingProvinces = false;
      }
    },
    async loadRegencies(provinceCode) {
      this.loadingRegencies = true;
      this.errorMessage = "";
      try {
        const res = await thestore.getKycRegionsRegencies(
          this.token,
          provinceCode
        );
        if (res?.success) {
          this.regencies = res.data || [];
        } else {
          this.regencies = [];
          this.errorMessage = res?.message || "Gagal memuat data kabupaten.";
        }
      } catch (error) {
        this.regencies = [];
        this.errorMessage =
          error?.response?.data?.message || "Gagal memuat data kabupaten.";
      } finally {
        this.loadingRegencies = false;
      }
    },
    async loadDistricts(regencyCode) {
      this.loadingDistricts = true;
      this.errorMessage = "";
      try {
        const res = await thestore.getKycRegionsDistricts(
          this.token,
          regencyCode
        );
        if (res?.success) {
          this.districts = res.data || [];
        } else {
          this.districts = [];
          this.errorMessage = res?.message || "Gagal memuat data kecamatan.";
        }
      } catch (error) {
        this.districts = [];
        this.errorMessage =
          error?.response?.data?.message || "Gagal memuat data kecamatan.";
      } finally {
        this.loadingDistricts = false;
      }
    },
    async loadVillages(districtCode) {
      this.loadingVillages = true;
      this.errorMessage = "";
      try {
        const res = await thestore.getKycRegionsVillages(
          this.token,
          districtCode
        );
        if (res?.success) {
          this.villages = res.data || [];
        } else {
          this.villages = [];
          this.errorMessage = res?.message || "Gagal memuat data kelurahan.";
        }
      } catch (error) {
        this.villages = [];
        this.errorMessage =
          error?.response?.data?.message || "Gagal memuat data kelurahan.";
      } finally {
        this.loadingVillages = false;
      }
    },
    onProvinceChange(event) {
      const code = event.target.value;
      const cleared = this.resetFrom("province");
      this.patch({
        ...cleared,
        provinceCode: code,
        provinceName: this.findName(this.provinces, code),
      });
      if (code) this.loadRegencies(code);
    },
    onRegencyChange(event) {
      const code = event.target.value;
      const cleared = this.resetFrom("regency");
      this.patch({
        ...cleared,
        regencyCode: code,
        regencyName: this.findName(this.regencies, code),
      });
      if (code) this.loadDistricts(code);
    },
    onDistrictChange(event) {
      const code = event.target.value;
      const cleared = this.resetFrom("district");
      this.patch({
        ...cleared,
        districtCode: code,
        districtName: this.findName(this.districts, code),
      });
      if (code) this.loadVillages(code);
    },
    onVillageChange(event) {
      const code = event.target.value;
      const village = this.villages.find(
        (item) => String(item.code) === String(code)
      );
      this.patch({
        ...this.resetFrom("village"),
        villageCode: code,
        villageName: village ? village.name : "",
      });
    },
    onPostalCodeInput(event) {
      const digits = String(event.target.value || "").replace(/\D/g, "").slice(0, 5);
      event.target.value = digits;
      this.patch({ postalCode: digits });
    },
    onStreetInput(event) {
      this.patch({ street: event.target.value });
    },
  },
};
</script>

<style scoped>
label {
  font-weight: 600;
  color: #dadada;
}

.form_input {
  padding: 8px 12px;
  background-color: #1e2435;
  border: 1px solid #2e3447;
  border-radius: 5px;
  color: #dadada;
  outline: none;
  margin-top: 4px;
}
.form_input:focus {
  border-color: #fedba1;
}
.form_input::placeholder {
  color: #5a5f72;
}
.form_input:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

select.form_input option {
  color: #1e2435;
  background-color: #ffffff;
}

textarea.form_input {
  resize: vertical;
  min-height: 4.5rem;
}

.kyc-address-error {
  color: #ff4c4c;
  font-size: 12px;
}
</style>
